import { useAuth, useTellescopeInfiniteQuery, useUsersMap } from "@tellescope/tanstack-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIsUserOutOfOfficeNow } from "../useIsUserOutOfOfficeNow";
import type { MentionTarget } from "../useMentions";
import { GroupOption } from "./GroupOption";
import { UserOption } from "./UserOption";

export interface MentionDropdownProps {
  query: string;
  onSelect: (target: MentionTarget) => void;
  onDismiss: () => void;
}

export const MentionDropdown = ({ onSelect, onDismiss }: MentionDropdownProps) => {
  const { t } = useTranslation("team-chat");

  const { selectedOrganization } = useAuth();
  const orgGroups = useMemo(() => selectedOrganization?.groups ?? [], [selectedOrganization?.groups]);

  const usersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: { limit: 50 },
    select: (data) => data.pages.flat(),
  });

  const usersMap = useUsersMap();
  const userPool = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const isUserOutOfOffice = useIsUserOutOfOfficeNow();

  return (
    <Popover
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <PopoverAnchor className="absolute bottom-full left-0 right-0" />
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        side="top"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onDismiss();
          }
        }}
      >
        <Command>
          <CommandInput autoFocus placeholder={t("Search users...")} />
          <CommandList>
            <CommandEmpty>{t("No users found.")}</CommandEmpty>
            {orgGroups.length > 0 && (
              <CommandGroup heading={t("Groups")}>
                {orgGroups.map((name) => (
                  <GroupOption
                    key={name}
                    name={name}
                    onSelect={(userIds) => onSelect({ type: "group", name, userIds })}
                  />
                ))}
              </CommandGroup>
            )}
            {userPool.length > 0 && (
              <CommandGroup heading={t("Users")}>
                {userPool.map((user) => {
                  const resolvedUser = usersMap[user.id] ?? user;
                  return (
                    <UserOption
                      key={user.id}
                      user={resolvedUser}
                      onSelect={() => onSelect({ type: "user", user: resolvedUser })}
                      isOutOfOffice={isUserOutOfOffice(resolvedUser)}
                    />
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
